import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

interface DatePickerProps {
  value?: Date | null;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Date format string consumed by date-fns. */
  displayFormat?: string;
  fromDate?: Date;
  toDate?: Date;
  /** Extra classes on the popover content — e.g. a z-index override for
   * hosts (like /cti2's full-screen shell) that sit above Radix's default
   * portal z-index. */
  contentClassName?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  disabled,
  className,
  displayFormat = 'MMM d, yyyy',
  fromDate,
  toDate,
  contentClassName,
}: DatePickerProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start gap-2 font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4" />
          {value ? format(value, displayFormat) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-auto p-0', contentClassName)} align="start">
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={onChange}
          {...(fromDate ? { fromDate } : {})}
          {...(toDate ? { toDate } : {})}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
